import {useContext, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {toast} from "react-toastify";
import AuthContext from "../../context/AuthContext";
import {
    getBillingPaymentMethod,
    setupBillingPaymentMethod,
    confirmBillingPaymentMethod,
    setDefaultBillingPaymentMethod,
    removeBillingPaymentMethod,
} from "../../api/api";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import Loader from "../../components/Loader";

const MAX_PAYMENT_METHODS = 5;

// DRF errors come back in several shapes ({detail}, ["msg"], {field: ["msg"]}, "msg").
const extractError = (err) => {
    const d = err?.response?.data;
    if (!d) return err?.message || 'Something went wrong.';
    if (typeof d === 'string') return d;
    if (d.detail) return d.detail;
    if (Array.isArray(d)) return d.join(' ');
    if (typeof d === 'object') {
        const first = Object.values(d)[0];
        return Array.isArray(first) ? first[0] : String(first);
    }
    return 'Something went wrong.';
};

function PaymentMethodTab() {
    const {refreshToken, user} = useContext(AuthContext);
    const queryClient = useQueryClient();
    // Which method's flow is in progress ('CARD' | 'EMANDATE' | null), so only the
    // clicked button shows a spinner.
    const [pendingMethod, setPendingMethod] = useState(null);

    const {data, refetch} = useQuery({
        queryKey: ['billing-payment-method'],
        queryFn: getBillingPaymentMethod,
        retry: false,
        refetchOnWindowFocus: false,
        onError: (err) => {
            handleUnauthorized401QueryError(err, refreshToken, refetch);
            if (err.response?.status !== 401) {
                toast.error(`An error occurred! ${extractError(err)}`);
            }
        },
    });

    const methods = Array.isArray(data) ? data : [];
    const atLimit = methods.length >= MAX_PAYMENT_METHODS;

    const confirmMutation = useMutation({
        mutationFn: confirmBillingPaymentMethod,
        onSuccess: () => {
            queryClient.invalidateQueries(['billing-payment-method']);
            queryClient.invalidateQueries(['user']);  // clears the billing-restriction banner
            toast.success('Payment method saved!');
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, confirmMutation, variables);
            if (err.response?.status !== 401) {
                toast.error(`An error occurred! ${extractError(err)}`);
            }
        },
        onSettled: () => setPendingMethod(null),
    });

    const setupMutation = useMutation({
        mutationFn: setupBillingPaymentMethod,
        onSuccess: (setupData) => {
            if (!window.Razorpay) {
                toast.error('Payment module failed to load. Please refresh and try again.');
                return;
            }
            const rzp = new window.Razorpay({
                key: setupData.key_id,
                order_id: setupData.order_id,
                customer_id: setupData.razorpay_customer_id,
                recurring: 1,
                name: 'BlackX',
                description: 'Save a payment method for usage-based billing',
                prefill: {
                    name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
                    email: user?.email || '',
                    contact: user?.phone || '',
                },
                theme: {color: '#4d5bed'},
                modal: {
                    // Merchant closed Checkout without paying — stop the spinner.
                    ondismiss: () => setPendingMethod(null),
                },
                handler: (response) => {
                    // confirmMutation clears pendingMethod in its onSettled.
                    confirmMutation.mutate({
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_signature: response.razorpay_signature,
                    });
                },
            });
            rzp.on('payment.failed', (response) => {
                toast.error(`Payment failed! ${response?.error?.description || ''}`);
                setPendingMethod(null);
            });
            rzp.open();
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, setupMutation, variables);
            if (err.response?.status !== 401) {
                toast.error(`An error occurred! ${extractError(err)}`);
            }
            setPendingMethod(null);
        },
    });

    const setDefaultMutation = useMutation({
        mutationFn: setDefaultBillingPaymentMethod,
        onSuccess: () => {
            queryClient.invalidateQueries(['billing-payment-method']);
            toast.success('Default payment method updated.');
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, setDefaultMutation, variables);
            if (err.response?.status !== 401) {
                toast.error(`An error occurred! ${extractError(err)}`);
            }
        },
    });

    const removeMutation = useMutation({
        mutationFn: removeBillingPaymentMethod,
        onSuccess: () => {
            queryClient.invalidateQueries(['billing-payment-method']);
            queryClient.invalidateQueries(['user']);  // removing the last method re-restricts
            toast.success('Payment method removed.');
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, removeMutation, variables);
            if (err.response?.status !== 401) {
                toast.error(`An error occurred! ${extractError(err)}`);
            }
        },
    });

    const handleAdd = (method) => {
        setPendingMethod(method);
        setupMutation.mutate({method});
    };

    const isAdding = pendingMethod !== null;
    const isMutating = setDefaultMutation.isLoading || removeMutation.isLoading;

    return (
        <div className="mt-3">
            <div className="mb-3 d-flex justify-content-between align-items-start"
                 style={{gap: '16px', flexWrap: 'wrap'}}>
                <div style={{minWidth: 0}}>
                    <h4 className="mb-1">Payment Methods</h4>
                    <p className="text-muted mb-0">
                        Add a card or bank eMandate so BlackX can charge you automatically based on your
                        monthly usage, up to ₹50,000 per charge. You can save up to {MAX_PAYMENT_METHODS}.
                    </p>
                </div>
                <div style={{display: 'flex', gap: '8px', flexShrink: 0, flexWrap: 'wrap'}}>
                    <button
                        className="btn bx-btn-primary"
                        style={{whiteSpace: 'nowrap'}}
                        onClick={() => handleAdd('CARD')}
                        disabled={isAdding || atLimit}
                        title={atLimit ? `You can save at most ${MAX_PAYMENT_METHODS} payment methods.` : undefined}
                    >
                        {pendingMethod === 'CARD' ? <><Loader size={'small'}/>Processing...</> : 'Add Card'}
                    </button>
                    <button
                        className="btn bx-btn-primary"
                        style={{whiteSpace: 'nowrap'}}
                        onClick={() => handleAdd('EMANDATE')}
                        disabled={isAdding || atLimit}
                        title={atLimit ? `You can save at most ${MAX_PAYMENT_METHODS} payment methods.` : undefined}
                    >
                        {pendingMethod === 'EMANDATE' ? <><Loader size={'small'}/>Processing...</> : 'Add Bank Account (eMandate)'}
                    </button>
                </div>
            </div>

            {methods.length === 0 ? (
                <div className="card border">
                    <div className="card-body">
                        <p className="mb-0">No payment method on file. Add one to enable automatic usage billing.</p>
                    </div>
                </div>
            ) : (
                methods.map((pm) => (
                    <div className="card border" key={pm.id}>
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-1">
                                        {pm.method === 'EMANDATE'
                                            ? `${pm.bank_name || 'Bank Account'} •••• ${pm.bank_account_last4 || '____'}`
                                            : `${pm.card_network || 'Card'} •••• ${pm.card_last4 || '____'}`}
                                        {pm.is_default && (
                                            <span className="badge bg-success" style={{marginLeft: '8px'}}>Default</span>
                                        )}
                                    </h5>
                                    <small className="text-muted">
                                        {pm.method === 'EMANDATE' ? 'Bank eMandate' : 'Card mandate'}
                                    </small>
                                </div>
                                <div>
                                    {!pm.is_default && (
                                        <button
                                            className="btn btn-outline-primary btn-sm"
                                            style={{marginRight: '8px'}}
                                            onClick={() => setDefaultMutation.mutate(pm.id)}
                                            disabled={isMutating}
                                        >
                                            Set as default
                                        </button>
                                    )}
                                    <button
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => removeMutation.mutate(pm.id)}
                                        disabled={isMutating || pm.is_default}
                                        title={pm.is_default ? 'Set another method as default before removing this one.' : undefined}
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}

export default PaymentMethodTab;
